"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createApptInfoMachine = exports.makeConfig = exports.machineJSON = void 0;
const xstate_1 = require("xstate");
const utils_1 = require("../../lib/utils");
const validation_1 = require("../../lib/constants/validation");
const States_1 = require("../../lib/constants/States");
const Actions_1 = require("../../lib/constants/Actions");
const api_1 = require("../../lib/api");
const gxUtils_1 = require("../../lib/gxUtils");
const gx_1 = require("../../lib/constants/gx");
const actions = {
    /**We set linked GX answers for use outside of apptinfo step.
     * Use cases include:
     *     - Some answers are bound for EHR
     *         - These are not to be shown again in the UI.
     *         - E.g., telehealth attestation is asked once by GX, and then the answer is passed to EHR.
     *     - Controls that have a `path` variable may be able to be linked to other parts of the ui.
     *         - E.g., An answer from GX for DOB could be used to auto-fill DOB in the patient reg form.
     */
    setLinkedGxAnswers: (0, xstate_1.assign)({
        linkedGxAnswers: (ctx, ev) => {
            const { steps } = ev.data;
            const linkedGxAnswers = ctx.linkedGxAnswers;
            const updatedLinkedGxAnswers = (0, gxUtils_1.getUpdatedLinkedGXAnswers)(steps, linkedGxAnswers);
            return updatedLinkedGxAnswers;
        }
    }),
    setPatientRel: (0, xstate_1.assign)({
        patientRel: (ctx, ev) => ev.data.patientRel,
        purpose: () => null
    }),
    setApptPurpose: (0, xstate_1.assign)({
        purpose: (ctx, ev) => ev.data.purpose
    }),
    sendParentNext: (0, xstate_1.sendParent)((ctx) => ({
        type: Actions_1.NEXT,
        data: {
            patientRel: ctx.patientRel,
            purpose: ctx.purpose,
            isCaptchaVerified: ctx.isCaptchaVerified,
            linkedGxAnswers: ctx.linkedGxAnswers
        }
    })),
    setIsCaptchaVerified: (0, xstate_1.assign)((ctx, ev) => ({
        isCaptchaVerified: ev.data
    })),
    deletePurposeFromCtx: (0, xstate_1.assign)(() => ({
        purpose: null
    })),
    setConditionalGxQuestions: (0, xstate_1.assign)((ctx, ev) => ({
        gx: Object.assign(Object.assign({}, ctx.gx), { v2Conditionals: ev.data })
    })),
    clearConditionalGxQuestions: (0, xstate_1.assign)((ctx) => ({
        gx: Object.assign(Object.assign({}, ctx.gx), { v2Conditionals: null })
    })),
    setGxPurposeOptionsByRel: (0, xstate_1.assign)({
        gxPurposeOptionsByRel: (ctx, ev) => {
            const { purposeOptionsByRel } = ctx;
            const appointmentPurposes = ev.data.curStep.appointmentPurposes;
            // if we don't get any purposes from GX, we should return null so the rest of the app will
            // default to using purposeOptionsByRel in the guards, transitions, UI, etc.
            if (!appointmentPurposes || !Array.isArray(appointmentPurposes)) {
                return null;
            }
            // returns { new: [...], established: [...] }, just like purposeOptionsByRel, with empty
            // arrays by default is a given patient relationship isn't in the data
            const apptPurposesFromGx = (0, utils_1.buildApptPurposesFromGxApptPurposes)(appointmentPurposes);
            // filter function to help us find the overlap between the purposes from the provider and
            // purposes returned by GX
            const filterMatchingPurposes = (providerPurposes, gxPurposes) => providerPurposes.filter((purpose) => gxPurposes.some((gxPurpose) => purpose.name === gxPurpose.name &&
                purpose.visit_method === gxPurpose.visit_method));
            return {
                new: filterMatchingPurposes(purposeOptionsByRel.new, apptPurposesFromGx.new),
                established: filterMatchingPurposes(purposeOptionsByRel.established, apptPurposesFromGx.established)
            };
        }
    }),
    /** Setting the context from which the GX backend will read and respond with relevant GX questons
     * - `current_section: 'conditional'` - a flag that notifies the GX backend we want conditional GX questions (vs non-conditional questions)
     * - `visit_method` - this is passed today as the only condition the GX backend checks on whether to return conditional GX questions (e.g. telehealth attestation feature)
     * NOTE: In the future, we may want to pass in more context (such as patient relationship) which could trigger different conditional GX questions
     */
    setConditionalGxContext: (0, xstate_1.assign)({
        gx: (ctx) => {
            var _a, _b, _c;
            const gxVisitMethod = (_b = (_a = ctx.purpose) === null || _a === void 0 ? void 0 : _a.visit_method) !== null && _b !== void 0 ? _b : null;
            const conditionalGXContext = {
                current_section: gx_1.GX_CONTEXT.CURRENT_SECTION.CONDITIONAL,
                visit_method: gxVisitMethod
            };
            const contextualInfo = {
                context: Object.assign(Object.assign({}, (_c = ctx.gx.contextualInfo) === null || _c === void 0 ? void 0 : _c.context), conditionalGXContext)
            };
            return Object.assign(Object.assign({}, ctx.gx), { contextualInfo });
        }
    }),
    /** clear the flag used to check if a call was made to GX API to check for conditional questions using relevant and up to date contextualInfo */
    clearGxConditionalContextualInfoSent: (0, xstate_1.assign)({
        gx: (ctx) => (Object.assign(Object.assign({}, ctx.gx), { conditionalContextualInfoSent: false }))
    }),
    setAltProviders: (0, xstate_1.assign)({
        altProviders: (_ctx, ev) => {
            return ev.data;
        }
    })
};
/**
 * @param {machine.context} ctx
 * @returns {Boolean}
 */
const ctxPurposeExists = (ctx) => {
    var _a;
    const { patientRel, purpose, purposeOptionsByRel, gxPurposeOptionsByRel } = ctx;
    if (!purpose) {
        return false;
    }
    // gxPurposeOptionsByRel is only defined when GX actually brings them back
    const purposeOptions = gxPurposeOptionsByRel || purposeOptionsByRel;
    const purposeExists = (_a = purposeOptions[patientRel]) === null || _a === void 0 ? void 0 : _a.find((v) => v.name === purpose.name);
    return Boolean(purposeExists);
};
const guards = {
    doesNotHavePurposes: (ctx) => {
        const { patientRel, purposeOptionsByRel, gxPurposeOptionsByRel } = ctx;
        const purposeOptions = gxPurposeOptionsByRel || purposeOptionsByRel;
        return purposeOptions[patientRel].length === 0;
    },
    hasNoAvailability: (ctx) => {
        const { purposesWithAvailability } = ctx;
        for (const key in purposesWithAvailability) {
            if (purposesWithAvailability[key].size) {
                return false;
            }
        }
        return true;
    },
    hasPatientRelAvailability: (ctx) => {
        const { purposesWithAvailability, patientRel } = ctx;
        return purposesWithAvailability[patientRel].size > 0;
    },
    hasPurposeAvailability: (ctx) => {
        const { purpose, purposesWithAvailability, patientRel } = ctx;
        return purposesWithAvailability[patientRel].has(purpose.name);
    },
    hasCtxPurposeWithAvailability: (ctx) => {
        const { patientRel, purpose, purposesWithAvailability } = ctx;
        const purposeExists = ctxPurposeExists(ctx);
        return (purposeExists && purposesWithAvailability[patientRel].has(purpose.name));
    },
    hasCtxPurposeWithNoAvailability: (ctx) => {
        const { patientRel, purpose, purposesWithAvailability } = ctx;
        const purposeExists = ctxPurposeExists(ctx);
        return (purposeExists && !purposesWithAvailability[patientRel].has(purpose.name));
    },
    hasGxQuestions: (ctx) => {
        if (ctx.skipGX) {
            return false;
        }
        return Boolean(ctx.gx.v1);
    },
    hasGxV2Questions: (ctx) => {
        if (ctx.skipGX) {
            return false;
        }
        return Boolean(ctx.gx.v2);
    },
    hasGxConditionalQuestions: (ctx) => {
        if (ctx.skipGX) {
            return false;
        }
        return Boolean(ctx.gx.v2Conditionals);
    },
    isCaptchaNotVerifiedSession: (_ctx, ev) => {
        return !ev.data;
    },
    isCaptchaVerifiedCtx: (ctx) => {
        return Boolean(ctx.isCaptchaVerified);
    },
    hasPatientRelInCtx: (ctx) => !!ctx.patientRel,
    hasNoConditionalGxQuestions: (ctx, ev) => {
        return !ev.data;
    },
    hasPatientRelAndPurposeWithAvailability: (ctx) => {
        const { patientRel, purpose, purposesWithAvailability } = ctx;
        const purposeExists = ctxPurposeExists(ctx);
        return (patientRel &&
            purposeExists &&
            purposesWithAvailability[patientRel].has(purpose.name));
    },
    conditionalGxContextualInfoNotSent: (ctx) => {
        return !ctx.gx.conditionalContextualInfoSent;
    },
    hasSuggestedProviders: (ctx, ev) => {
        var _a;
        return ((_a = ev.data) === null || _a === void 0 ? void 0 : _a.length) > 0;
    },
    shouldDisplayAltProviders: (ctx) => {
        return ctx.suggestAltProviderEnabled;
    }
};
const services = {
    getConditionalGxStepByProviderId: (ctx) => __awaiter(void 0, void 0, void 0, function* () {
        const { customerCode, productName, apiBaseURL } = ctx;
        const api = new api_1.Api({
            customerCode,
            productName,
            apiBaseURL
        });
        return yield api.getGxStepByProviderId(ctx.provider.id, ctx.gx.contextualInfo);
    }),
    checkCaptchaVerified: (ctx) => __awaiter(void 0, void 0, void 0, function* () {
        const { customerCode, productName, apiBaseURL } = ctx;
        const api = new api_1.Api({
            customerCode,
            productName,
            apiBaseURL
        });
        const res = yield api.checkCaptcha();
        return res.verified;
    }),
    getAltProviders: (ctx) => __awaiter(void 0, void 0, void 0, function* () {
        const { customerCode, provider, services, purpose, locationId, productName, apiBaseURL } = ctx;
        const { getAltProviders } = services;
        // Filter locations from provider object
        let locations = provider.locations;
        // First try to filter by locationId if available
        if (locationId) {
            const locationMatches = provider.locations.filter((location) => location.id.toString() === locationId.toString());
            locations =
                locationMatches.length > 0 ? locationMatches : provider.locations;
        }
        else {
            // Extract location parameters from URL
            const urlParams = new URLSearchParams(window.location.search);
            const locationParam = decodeURIComponent(urlParams.get('location') || '');
            const searchCoordinates = decodeURIComponent(urlParams.get('search_coordinates') || '');
            // If location & search_coordinates parameter exists and coordinates are valid
            if (locationParam &&
                searchCoordinates &&
                validation_1.LAT_LON_REGEX.test(searchCoordinates)) {
                const [lat, lon] = searchCoordinates.split(',');
                locations = [
                    {
                        coordinates: {
                            lat: parseFloat(lat),
                            lon: parseFloat(lon)
                        }
                    }
                ];
            }
        }
        // for now we are moving with patientRel as 'new' for all cases as per the requirement from product team
        // in future we can change this to dynamic based on the user selection via accessing patientRel from ctx
        const queryParams = {
            per_page: 3,
            patient_relationship: 'new',
            has_availability: true
        };
        if (purpose === null || purpose === void 0 ? void 0 : purpose.name) {
            queryParams.purpose = purpose.name;
        }
        // TODO this locations data comes from the provider, NOT the user selection.. should that change? (https://kyruus.jira.com/browse/KENG-36496)
        const api = new api_1.Api({ customerCode, productName, apiBaseURL });
        return getAltProviders
            ? yield api.getAltProvidersData(getAltProviders, provider, locations, queryParams)
            : null;
    })
};
/**
 * Method to return the altProvider states if the suggestAltProviderEnabled is true
 *
 * @param {boolean} shouldAttachSubState
 * @returns {object}
 */
const altProviderStates = {
    initial: States_1.initializing,
    states: {
        [States_1.initializing]: {
            always: [
                {
                    cond: 'shouldDisplayAltProviders',
                    target: States_1.fetchingAltProviders
                },
                {
                    target: States_1.idle
                }
            ]
        },
        [States_1.fetchingAltProviders]: {
            invoke: {
                src: 'getAltProviders',
                onDone: [
                    {
                        actions: 'setAltProviders',
                        cond: 'hasSuggestedProviders',
                        target: States_1.suggestedProviders
                    },
                    {
                        target: States_1.noSuggestedProviders
                    }
                ],
                // error is not fatal here, page can continue
                onError: States_1.noSuggestedProviders
            }
        },
        [States_1.suggestedProviders]: {},
        [States_1.noSuggestedProviders]: {},
        [States_1.idle]: {}
    }
};
// This is exposed for snapshot testing
const machineJSON = ({ provider, isCaptchaVerified, csrfToken, patientRel, purpose, appointmentQuery, gx, hidePatientRelConfig, services, visibility, featureFlags, appointmentEhrId, locationId, suggestAltProviderEnabled, searchMatchEnabled, apiBaseURL, gxPurposeOptionsByRel // passing in for testing purposes only, doesn't really get passed in like this
 }) => ({
    id: `Machine__${States_1.apptInfo}`,
    type: 'parallel',
    context: {
        purposesWithAvailability: provider.availableApptPurposes,
        purposeOptionsByRel: (0, utils_1.getPurposeOptionsByPatientRel)(provider, visibility.schedulingVisibility),
        provider,
        isCaptchaVerified,
        patientRel: appointmentQuery.patientRel
            ? appointmentQuery.patientRel
            : patientRel,
        purpose: appointmentQuery.purpose ? appointmentQuery.purpose : purpose,
        purposeTerms: [],
        csrfToken,
        visibility,
        gx,
        gxPurposeOptionsByRel,
        linkedGxAnswers: {},
        hidePatientRel: (0, utils_1.hidePatientRel)(provider, hidePatientRelConfig),
        services,
        featureFlags,
        appointmentEhrId,
        locationId,
        suggestAltProviderEnabled,
        searchMatchEnabled,
        apiBaseURL
    },
    states: {
        // Gx controlled specific questions
        [States_1.gxQuestions]: {
            initial: States_1.routing,
            states: {
                [States_1.routing]: {
                    always: [
                        { cond: 'hasNoAvailability', target: States_1.noAvailability },
                        { cond: 'hasGxQuestions', target: States_1.apptGxQuestions },
                        { cond: 'hasGxV2Questions', target: States_1.apptGxV2Questions },
                        { target: States_1.noResults }
                    ]
                },
                [States_1.noAvailability]: {},
                [States_1.noResults]: {},
                // gxQuestions.apptGxQuestions
                [States_1.apptGxQuestions]: {
                    initial: States_1.idle,
                    on: {
                        [Actions_1.GX_QUESTION_ANSWERED]: `.${States_1.idle}`,
                        [Actions_1.GX_PASSED]: {
                            target: `.${States_1.passedGx}`,
                            actions: ['setGxPurposeOptionsByRel']
                        },
                        [Actions_1.GX_V1_FAILED]: `.${States_1.failedGxV1}`
                    },
                    states: {
                        [States_1.idle]: {},
                        [States_1.passedGx]: {},
                        [States_1.failedGxV1]: {}
                    }
                },
                // gxQuestions.apptGxV2Questions
                [States_1.apptGxV2Questions]: {
                    initial: States_1.idle,
                    on: {
                        [Actions_1.GX_PASSED]: {
                            actions: ['setLinkedGxAnswers', 'setGxPurposeOptionsByRel']
                        }
                    },
                    states: {
                        [States_1.idle]: {}
                    }
                }
            }
        },
        // patient relationship, appointment purpose, and conditional Gx questions; recaptcha
        [States_1.apptQuestions]: {
            initial: States_1.routing,
            on: {
                [Actions_1.SET_PATIENT_REL]: {
                    target: `.${States_1.apptPatientRel}.${States_1.routing}`,
                    actions: 'setPatientRel'
                },
                [Actions_1.SET_APPT_PURPOSE]: {
                    target: `.${States_1.apptPurpose}.${States_1.routing}`,
                    actions: [
                        'setApptPurpose',
                        // a purpose selection will update the conditions that conditional Gx questions toggles on
                        'clearGxConditionalContextualInfoSent'
                    ]
                },
                // if a user changes their Gx answer,
                // then we'll go back to an idle state to wait for a "passed" gx validation
                [Actions_1.GX_QUESTION_ANSWERED]: `.${States_1.idle}`,
                // GX v1's `onChange` is only called up until a GX v1 question set has been completed,
                // then `onDone` is be called with any user answer change after that.
                // Therefore, this event listener remains here until GX v1 retires to the white sands of Ipanema Beach.
                [Actions_1.GX_V1_FAILED]: `.${States_1.idle}`
            },
            states: {
                [States_1.routing]: {
                    always: [
                        { cond: 'hasNoAvailability', target: States_1.apptPatientRel },
                        { cond: 'hasGxQuestions', target: States_1.idle },
                        {
                            cond: 'hasGxV2Questions',
                            target: States_1.idle
                        },
                        {
                            // cond may be true w/ deepLinking
                            cond: 'hasPatientRelAndPurposeWithAvailability',
                            target: States_1.conditionalGxQuestions
                        },
                        { target: States_1.apptPatientRel }
                    ]
                },
                // apptQuestions.idle
                [States_1.idle]: {
                    on: {
                        [Actions_1.GX_PASSED]: {
                            target: States_1.apptPatientRel,
                            actions: ['setGxPurposeOptionsByRel']
                        }
                    }
                },
                // apptQuestions.apptPatientRel
                [States_1.apptPatientRel]: {
                    initial: States_1.idle,
                    states: {
                        [States_1.idle]: {
                            always: [
                                {
                                    cond: 'hasPatientRelInCtx',
                                    target: States_1.routing
                                }
                            ]
                        },
                        [States_1.noAvailability]: altProviderStates,
                        [States_1.routing]: {
                            always: [
                                { cond: 'hasNoAvailability', target: States_1.noAvailability },
                                { cond: 'hasPatientRelAvailability', target: States_1.done },
                                { target: States_1.noAvailability }
                            ]
                        },
                        [States_1.done]: {
                            type: 'final'
                        }
                    },
                    onDone: States_1.apptPurpose
                },
                // apptQuestions.apptPurpose
                [States_1.apptPurpose]: {
                    initial: States_1.pending,
                    states: {
                        [States_1.routing]: {
                            always: [
                                { cond: 'hasPurposeAvailability', target: States_1.done },
                                { target: States_1.noAvailability }
                            ]
                        },
                        [States_1.noAvailability]: altProviderStates,
                        [States_1.idle]: {},
                        [States_1.noResults]: altProviderStates,
                        [States_1.pending]: {
                            always: [
                                { cond: 'doesNotHavePurposes', target: States_1.noResults },
                                {
                                    // we have a `purpose` with availability in `ctx`, because the user
                                    // selected it or it was passed by deepLinking
                                    cond: 'hasCtxPurposeWithAvailability',
                                    target: States_1.done
                                },
                                {
                                    cond: 'hasCtxPurposeWithNoAvailability',
                                    target: States_1.noAvailability
                                },
                                {
                                    target: States_1.idle,
                                    actions: 'deletePurposeFromCtx'
                                }
                            ]
                        },
                        [States_1.done]: {
                            type: 'final'
                        }
                    },
                    onDone: {
                        target: States_1.conditionalGxQuestions
                    }
                },
                // apptQuestions.conditionalGxQuestions
                [States_1.conditionalGxQuestions]: {
                    initial: States_1.routing,
                    states: {
                        [States_1.routing]: {
                            always: [
                                // this condition is true for the following cases:
                                // 1) non-deepLinked scenario
                                //     - need to make gx conditional call
                                // 2) deepLinked scenario #1: the user selected a new purpose
                                //     - need to make gx conditional call
                                {
                                    cond: 'conditionalGxContextualInfoNotSent',
                                    target: States_1.pending
                                },
                                // deepLinked scenario #2: already conditionalContextualInfoSent and got conditional questions
                                //     - no need to make gx conditional call again
                                {
                                    cond: 'hasGxConditionalQuestions',
                                    target: States_1.conditionalApptGxV2Questions
                                },
                                // deepLinked scenario #3: conditionalContextualInfoSent but no conditional questions
                                //     - no need to make gx conditional call again nor render conditionals
                                { target: States_1.done }
                            ]
                        },
                        [States_1.pending]: {
                            entry: 'setConditionalGxContext',
                            invoke: {
                                id: 'getConditionalGxStepByProviderId',
                                src: 'getConditionalGxStepByProviderId',
                                onDone: [
                                    {
                                        cond: 'hasNoConditionalGxQuestions',
                                        actions: 'clearConditionalGxQuestions',
                                        target: States_1.done
                                    },
                                    {
                                        actions: 'setConditionalGxQuestions',
                                        target: States_1.conditionalApptGxV2Questions
                                    }
                                ],
                                onError: States_1.error
                            }
                        },
                        [States_1.conditionalApptGxV2Questions]: {
                            initial: States_1.idle,
                            on: {
                                [Actions_1.CONDITIONAL_GX_PASSED]: {
                                    target: `.${States_1.passedGx}`,
                                    actions: ['setLinkedGxAnswers']
                                }
                            },
                            states: {
                                [States_1.idle]: {},
                                [States_1.passedGx]: { type: 'final' }
                            },
                            onDone: {
                                target: States_1.done
                            }
                        },
                        [States_1.error]: {},
                        [States_1.done]: {
                            type: 'final'
                        }
                    },
                    onDone: {
                        target: States_1.apptInfoComplete
                    }
                },
                // apptQuestions.apptInfoComplete
                [States_1.apptInfoComplete]: {
                    initial: States_1.idle,
                    on: {
                        [Actions_1.CONDITIONAL_GX_QUESTION_ANSWERED]: `${States_1.conditionalGxQuestions}.${States_1.conditionalApptGxV2Questions}`
                    },
                    states: {
                        [States_1.idle]: {
                            on: {
                                [Actions_1.NEXT]: [
                                    /**
                                     * if navigating back after having already verified captcha,
                                     * then the verified flag will be in ctx
                                     */
                                    { cond: 'isCaptchaVerifiedCtx', actions: 'sendParentNext' },
                                    { target: States_1.checkingCaptchaSession }
                                ]
                            }
                        },
                        [States_1.checkingCaptchaSession]: {
                            invoke: {
                                id: 'checkingCaptchaVerified',
                                src: 'checkCaptchaVerified',
                                onDone: [
                                    {
                                        cond: 'isCaptchaNotVerifiedSession',
                                        target: States_1.verifyingCaptcha
                                    },
                                    { actions: ['setIsCaptchaVerified', 'sendParentNext'] }
                                ],
                                onError: States_1.verifyingCaptcha
                            }
                        },
                        [States_1.verifyingCaptcha]: {
                            on: {
                                [Actions_1.CAPTCHA_CANCELLED]: {
                                    target: States_1.idle
                                },
                                [Actions_1.CAPTCHA_VERIFIED]: {
                                    actions: ['setIsCaptchaVerified', 'sendParentNext'],
                                    target: States_1.idle
                                },
                                [Actions_1.CAPTCHA_ERROR]: {
                                    target: States_1.verifyingCaptchaError
                                }
                            }
                        },
                        [States_1.verifyingCaptchaError]: {
                            on: {
                                [Actions_1.CAPTCHA_RESET]: {
                                    target: States_1.checkingCaptchaSession
                                },
                                [Actions_1.CAPTCHA_CANCELLED]: {
                                    target: States_1.idle
                                }
                            }
                        }
                    }
                }
            }
        }
    }
});
exports.machineJSON = machineJSON;
const makeConfig = () => ({ services, actions, guards });
exports.makeConfig = makeConfig;
const createApptInfoMachine = (_a) => {
    var { config = (0, exports.makeConfig)() } = _a, props = __rest(_a, ["config"]);
    return (0, xstate_1.Machine)((0, exports.machineJSON)(props)).withConfig(config);
};
exports.createApptInfoMachine = createApptInfoMachine;
//# sourceMappingURL=apptInfo.js.map